import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import PortalShell from './layouts/PortalShell';
import LoginPage from './pages/LoginPage';
import GlobalHome from './pages/GlobalHome';
import ModuleHome from './pages/ModuleHome';
import { useAuth } from './auth/AuthProvider';

const S: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Suspense
    fallback={
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
        <CircularProgress size={24} />
      </Box>
    }
  >
    {children}
  </Suspense>
);

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

// ===== Event Monitor (built-in) =====
const EventMonitorPage = lazy(() => import('./modules/events/pages/EventMonitorPage'));

// ===== Forge Admin Dashboard =====
const ForgeAdminPage = lazy(() => import('./modules/forge/ForgeAdminPage'));

// ===== Lazy-imported codegen pages from service frontends =====
const LivestockDashboard = lazy(() => import('@live/dashboard/DashboardPage'));
const LivestockAnimal = lazy(() => import('@live/pages/AnimalPage').then((m) => ({ default: m.AnimalPage || m.default })));
const LivestockBirthRecord = lazy(() => import('@live/pages/BirthRecordPage').then((m) => ({ default: m.BirthRecordPage || m.default })));
const LivestockBreedingRecord = lazy(() => import('@live/pages/BreedingRecordPage').then((m) => ({ default: m.BreedingRecordPage || m.default })));
const LivestockComplianceReport = lazy(() => import('@live/pages/ComplianceReportPage').then((m) => ({ default: m.ComplianceReportPage || m.default })));
const LivestockFarm = lazy(() => import('@live/pages/FarmPage').then((m) => ({ default: m.FarmPage || m.default })));
const LivestockFeedInventory = lazy(() => import('@live/pages/FeedInventoryPage').then((m) => ({ default: m.FeedInventoryPage || m.default })));
const LivestockFinancialTransaction = lazy(() => import('@live/pages/FinancialTransactionPage').then((m) => ({ default: m.FinancialTransactionPage || m.default })));
const LivestockHealthEvent = lazy(() => import('@live/pages/HealthEventPage').then((m) => ({ default: m.HealthEventPage || m.default })));
const LivestockMedicationStock = lazy(() => import('@live/pages/MedicationStockPage').then((m) => ({ default: m.MedicationStockPage || m.default })));
const LivestockMilkRecord = lazy(() => import('@live/pages/MilkRecordPage').then((m) => ({ default: m.MilkRecordPage || m.default })));
const LivestockMovement = lazy(() => import('@live/pages/MovementPage').then((m) => ({ default: m.MovementPage || m.default })));
const LivestockPaddock = lazy(() => import('@live/pages/PaddockPage').then((m) => ({ default: m.PaddockPage || m.default })));
const LivestockTask = lazy(() => import('@live/pages/TaskPage').then((m) => ({ default: m.TaskPage || m.default })));
const LivestockTreatment = lazy(() => import('@live/pages/TreatmentPage').then((m) => ({ default: m.TreatmentPage || m.default })));
const LivestockWeightRecord = lazy(() => import('@live/pages/WeightRecordPage').then((m) => ({ default: m.WeightRecordPage || m.default })));
const LivestockEquipment = lazy(() => import('@live/pages/EquipmentPage').then((m) => ({ default: m.EquipmentPage || m.default })));


// ===== Orchestrator (Chief Service) =====
const OrchestratorPage = lazy(() => import('@chief/components/orchestrator/OrchestratorPage'));

// Wiki
const WikiIndexPage = lazy(() => import('./modules/wiki/pages/WikiIndexPage'));
const WikiPageView = lazy(() => import('./modules/wiki/pages/WikiPageView'));
const WikiIngestPanel = lazy(() => import('./modules/wiki/pages/WikiIngestPanel'));
const WikiLintReport = lazy(() => import('./modules/wiki/pages/WikiLintReport'));

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      <Route
        path="/"
        element={
          <ProtectedRoute>
            <PortalShell />
          </ProtectedRoute>
        }
      >
        <Route index element={<GlobalHome />} />
        <Route path="dashboard" element={<S><GlobalHome /></S>} />

{/* Events module */}
        <Route path="events" element={<S><EventMonitorPage /></S>} />

{/* Forge admin */}
        <Route path="forge/admin" element={<S><ForgeAdminPage /></S>} />

{/* Module home pages */}
          <Route path="livestock" element={<S><ModuleHome moduleId="livestock" /></S>} />
          <Route path="chief" element={<S><ModuleHome moduleId="chief" /></S>} />
          <Route path="chief/orchestrator" element={<S><OrchestratorPage /></S>} />

{/* Admin CRUD pages (from service frontends) */}
          <Route path="livestock/dashboard" element={<S><LivestockDashboard /></S>} />
          <Route path="livestock/admin/animals" element={<S><LivestockAnimal /></S>} />
          <Route path="livestock/admin/birth-records" element={<S><LivestockBirthRecord /></S>} />
          <Route path="livestock/admin/breeding-records" element={<S><LivestockBreedingRecord /></S>} />
          <Route path="livestock/admin/compliance-reports" element={<S><LivestockComplianceReport /></S>} />
          <Route path="livestock/admin/farms" element={<S><LivestockFarm /></S>} />
          <Route path="livestock/admin/feed-inventories" element={<S><LivestockFeedInventory /></S>} />
          <Route path="livestock/admin/financial-transactions" element={<S><LivestockFinancialTransaction /></S>} />
          <Route path="livestock/admin/health-events" element={<S><LivestockHealthEvent /></S>} />
          <Route path="livestock/admin/medication-stocks" element={<S><LivestockMedicationStock /></S>} />
          <Route path="livestock/admin/milk-records" element={<S><LivestockMilkRecord /></S>} />
          <Route path="livestock/admin/movements" element={<S><LivestockMovement /></S>} />
          <Route path="livestock/admin/paddocks" element={<S><LivestockPaddock /></S>} />
          <Route path="livestock/admin/tasks" element={<S><LivestockTask /></S>} />
          <Route path="livestock/admin/treatments" element={<S><LivestockTreatment /></S>} />
          <Route path="livestock/admin/weight-records" element={<S><LivestockWeightRecord /></S>} />
          <Route path="livestock/admin/equipments" element={<S><LivestockEquipment /></S>} />
      
      {/* Wiki */}
      <Route path="wiki" element={<S><WikiIndexPage /></S>} />
      <Route path="wiki/page/:slug" element={<S><WikiPageView /></S>} />
      <Route path="wiki/ingest" element={<S><WikiIngestPanel /></S>} />
      <Route path="wiki/lint" element={<S><WikiLintReport /></S>} />
</Route>
    </Routes>
  );
}
